import { useState, useRef, useEffect } from "react";
import { sendAdminEmail } from "@/utils/emailService";

interface WorkshopEnquiryModalProps {
  isOpen: boolean;
  workshopTitle: string;
  onClose: () => void;
}

export default function WorkshopEnquiryModal({ isOpen, workshopTitle, onClose }: WorkshopEnquiryModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    institution: "",
    role: "",
    message: "",
    honeypot: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      // Reset form when closed
      setStatus("idle");
      setErrors({});
      setFormData({ name: "", email: "", phone: "", institution: "", role: "", message: "", honeypot: "" });
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Valid email is required.";
    if (!formData.phone.trim() || !/^[6-9]\d{9}$/.test(formData.phone.replace(/\s/g, "")))
      newErrors.phone = "Valid 10-digit Indian mobile number required.";
    if (!formData.institution.trim()) newErrors.institution = "Institution name is required.";
    if (!formData.role.trim()) newErrors.role = "Please select your role.";
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.honeypot) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus("submitting");

    const body = new URLSearchParams({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      institution: formData.institution,
      role: formData.role,
      workshop: workshopTitle,
      message: formData.message,
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d76livcbmgf2o8mm7a7g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (res.ok) {
        // Send admin notification email
        await sendAdminEmail({
          subject: `Workshop Enquiry - ${workshopTitle} from ${formData.institution}`,
          formType: 'Workshop Enquiry',
          replyTo: formData.email,
          fields: {
            'Workshop': workshopTitle,
            'Name': formData.name,
            'Email': formData.email,
            'Phone': formData.phone,
            'Institution': formData.institution,
            'Role': formData.role,
            'Message': formData.message || 'N/A',
          },
        });
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
        style={{ animation: "modalSlideIn 0.25s ease-out" }}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white z-10 px-8 pt-8 pb-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-600 text-lg"></i>
          </button>

          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-100">
              <i className="ri-calendar-schedule-line text-orange-600 text-xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Schedule Workshop</h3>
              <p className="text-sm text-orange-600 font-medium">{workshopTitle}</p>
            </div>
          </div>
          <p className="text-gray-500 text-sm mt-2">
            Fill in your details and our team will reach out within <strong>30 minutes</strong>.
          </p>
        </div>

        {/* Modal Body */}
        <div className="px-8 py-6">
          {status === "success" ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-double-line text-3xl text-emerald-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Enquiry Submitted!</h4>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Thank you for your interest in <strong>{workshopTitle}</strong>. Our team will contact you within 30 minutes during business hours.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-xl px-4 py-3 mb-6">
                <i className="ri-mail-check-line text-lg"></i>
                A confirmation has been sent to your email.
              </div>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form
              noValidate
              className="space-y-4"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              {/* Workshop (pre-filled, read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Workshop</label>
                <input
                  type="text"
                  value={workshopTitle}
                  readOnly
                  className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 bg-orange-50 text-orange-700 font-medium focus:outline-none"
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Dr. Rajesh Kumar"
                  className={`w-full px-4 py-3 text-sm rounded-xl border ${errors.name ? "border-red-400 bg-red-50" : "border-gray-200"} focus:outline-none focus:border-emerald-500 transition-colors`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@institution.edu"
                    className={`w-full px-4 py-3 text-sm rounded-xl border ${errors.email ? "border-red-400 bg-red-50" : "border-gray-200"} focus:outline-none focus:border-emerald-500 transition-colors`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10-digit mobile"
                    maxLength={10}
                    className={`w-full px-4 py-3 text-sm rounded-xl border ${errors.phone ? "border-red-400 bg-red-50" : "border-gray-200"} focus:outline-none focus:border-emerald-500 transition-colors`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>

              {/* Institution */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Institution Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                  placeholder="e.g. St. Mary&apos;s Higher Secondary School"
                  className={`w-full px-4 py-3 text-sm rounded-xl border ${errors.institution ? "border-red-400 bg-red-50" : "border-gray-200"} focus:outline-none focus:border-emerald-500 transition-colors`}
                />
                {errors.institution && <p className="text-red-500 text-xs mt-1">{errors.institution}</p>}
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Role <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 text-sm rounded-xl border ${errors.role ? "border-red-400 bg-red-50" : "border-gray-200"} focus:outline-none focus:border-emerald-500 transition-colors bg-white cursor-pointer`}
                >
                  <option value="">Select your role</option>
                  <option value="Principal / Director">Principal / Director</option>
                  <option value="Teacher / Faculty">Teacher / Faculty</option>
                  <option value="Corporate HR / CSR">Corporate HR / CSR</option>
                  <option value="College Dean / HOD">College Dean / HOD</option>
                  <option value="Government Official">Government Official</option>
                  <option value="Other">Other</option>
                </select>
                {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Requirements <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your institution&apos;s needs, preferred dates, number of students..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 text-right">{formData.message.length}/500</p>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line text-lg"></i>
                    Schedule This Workshop
                  </>
                )}
              </button>

              {status === "error" && (
                <p className="text-red-500 text-xs text-center">
                  Something went wrong. Please try again or email us at info@stemforgetechlabs.in
                </p>
              )}

              <p className="text-xs text-gray-400 text-center">
                By submitting, you agree to be contacted by STEMForge Tech Labs. We respect your privacy.
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(-20px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
